import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import LanguageSelector from "../LanguageSelector";
import ThemeToggle from "../ThemeToggle";
import { BackgroundSyncIndicator } from "../BackgroundSyncIndicator";
import JessToggleButton from "../JessToggleButton";
import TacitsBadge from "../TacitsBadge/TacitsBadge";
import { useFeatureFlags } from "../../contexts/FeatureFlagsContext";
import { SessionSyncMap } from "../../types/chats";
import styles from "./ResponsiveNavbar.module.scss";

interface ResponsiveNavbarProps {
  isConnected: boolean;
  connection: { status: string; message: string };
  isDarkTheme: boolean;
  onThemeToggle: () => void;
  isSidebarCollapsed?: boolean;
  profileImage?: string;
  profileName?: string;
  agentConnectionStatus?: {
    isAgentConnected: boolean;
    companyId: string;
    timestamp: number;
  } | null;
  showBackButton?: boolean;
  onBackClick?: () => void;
  onMenuClick?: () => void;
  hideHamburgerMenu?: boolean;
  syncingSessions?: SessionSyncMap;
}

export const ResponsiveNavbar: React.FC<ResponsiveNavbarProps> = ({
  isConnected,
  connection,
  isDarkTheme,
  onThemeToggle,
  isSidebarCollapsed = false,
  profileImage = "/images/jess-profile.png",
  profileName,
  agentConnectionStatus = null,
  showBackButton = false,
  onBackClick,
  onMenuClick,
  hideHamburgerMenu = false,
  syncingSessions,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isMobile, isTablet } = useBreakpoint();
  const { hasFlag } = useFeatureFlags();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const toggleMenu = () => {
    if (isMenuOpen) {
      // Start closing animation
      setIsClosing(true);
      // Close after animation completes (300ms)
      setTimeout(() => {
        setIsMenuOpen(false);
        setIsClosing(false);
      }, 300);
    } else {
      setIsMenuOpen(true);
      setIsClosing(false);
    }
  };

  const changeLanguage = (language: string) => {
    // Para mobile, usar a funcionalidade local do componente
    i18n.changeLanguage(language);
    localStorage.setItem("language", language);

    // Dispatch evento global para sincronizar
    window.dispatchEvent(new Event("languageChanged"));

    // Close menu with animation
    setIsClosing(true);
    setTimeout(() => {
      setIsMenuOpen(false);
      setIsClosing(false);
    }, 300);
  };

  const handleThemeToggle = () => {
    onThemeToggle();
    // Close menu with animation
    setIsClosing(true);
    setTimeout(() => {
      setIsMenuOpen(false);
      setIsClosing(false);
    }, 300);
  };

  // Get connection status text and color
  const getConnectionStatusDisplay = () => {
    if (agentConnectionStatus) {
      return {
        text: agentConnectionStatus.isAgentConnected
          ? t("chatjess.status.online") || "online"
          : t("chatjess.status.offline") || "offline",
        isOnline: agentConnectionStatus.isAgentConnected,
      };
    }
    return {
      text: t("chatjess.status.offline") || "offline",
      isOnline: false,
    };
  };

  const handleBackClick = () => {
    if (onBackClick) {
      onBackClick();
    } else {
      navigate(-1);
    }
  };

  const handleMenuClick = () => {
    if (onMenuClick) {
      onMenuClick();
    } else {
      // Dispatch custom event para abrir o sidebar
      window.dispatchEvent(
        new CustomEvent("sidebarToggle", {
          detail: { isOpen: true },
        }),
      );
    }
  };

  if (isMobile || isTablet) {
    return (
      <div
        className={`${styles.mobileNavbar} ${isSidebarCollapsed ? styles.expanded : ""} ${!isDarkTheme ? styles.lightTheme : ""}`}
      >
        {/* Hamburger Menu Button - Hidden when chat is open */}
        {!hideHamburgerMenu && (
          <button
            className={styles.menuButton}
            onClick={handleMenuClick}
            aria-label="Menu"
          >
            <i className="fas fa-bars" />
          </button>
        )}

        {/* Back Button - Shows when navigation back is needed */}
        {showBackButton && (
          <button
            className={styles.backButton}
            onClick={handleBackClick}
            aria-label="Voltar"
          >
            <i className="fas fa-arrow-left" />
          </button>
        )}

        {/* Profile Section - Always visible */}
        <div className={styles.profileSection}>
          <div className={styles.profileImageWrapper}>
            <img
              src={profileImage}
              alt="Jess"
              className={styles.profileImage}
            />
            <span
              className={`${styles.statusBadge} ${getConnectionStatusDisplay().isOnline ? styles.online : styles.offline}`}
            >
              {getConnectionStatusDisplay().text}
            </span>
          </div>
          <div className={styles.profileInfo}>
            {profileName ? (
              <img
                src={profileName}
                alt="Jesss"
                className={styles.profileNameImage}
              />
            ) : (
              <span className={styles.profileName}>Jesss</span>
            )}
          </div>
          {hasFlag("tacits") && <TacitsBadge isDarkTheme={isDarkTheme} />}
        </div>

        {/* Settings Menu Button */}
        <button
          className={`${styles.hamburgerButton} ${isMenuOpen ? styles.open : ""}`}
          onClick={toggleMenu}
          aria-label="Configurações"
        >
          <i className="fas fa-cog" />
        </button>

        {/* Mobile Menu Overlay */}
        {isMenuOpen && (
          <>
            <div className={styles.menuOverlay} onClick={toggleMenu} />
            <div
              className={`${styles.mobileMenu} ${!isDarkTheme ? styles.lightTheme : ""} ${isClosing ? styles.closing : ""}`}
            >
              {/* Language Selector - Mobile Version (mantendo a atual) */}
              <div className={styles.menuItem}>
                <span className={styles.menuLabel}>
                  {t("settingsLayout.language")}
                </span>
                <div className={styles.languageButtons}>
                  <button
                    className={`${styles.languageButton} ${i18n.language === "pt" ? styles.active : ""}`}
                    onClick={() => changeLanguage("pt")}
                  >
                    🇧🇷 PT
                  </button>
                  <button
                    className={`${styles.languageButton} ${i18n.language === "en" ? styles.active : ""}`}
                    onClick={() => changeLanguage("en")}
                  >
                    🇺🇸 EN
                  </button>
                  <button
                    className={`${styles.languageButton} ${i18n.language === "es" ? styles.active : ""}`}
                    onClick={() => changeLanguage("es")}
                  >
                    🇪🇸 ES
                  </button>
                </div>
              </div>

              {/* Theme Toggle - Mobile Version */}
              <div className={styles.menuItem}>
                <span className={styles.menuLabel}>
                  {t("settingsLayout.theme")}
                </span>
                <div className={styles.themeToggleWrapper}>
                  <ThemeToggle
                    isDarkTheme={isDarkTheme}
                    onToggle={handleThemeToggle}
                  />
                </div>
              </div>

              {/* Jess AI Toggle - Mobile Version */}
              <div className={styles.menuItem}>
                <span className={styles.menuLabel}>
                  {t("ai_enabled") || "Jess AI"}
                </span>
                <div className={styles.themeToggleWrapper}>
                  <JessToggleButton isDarkTheme={isDarkTheme} />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Desktop Layout - Using Global LanguageSelector
  return (
    <div
      className={`${styles.desktopNavbar} ${isSidebarCollapsed ? styles.expanded : ""} ${!isDarkTheme ? styles.lightTheme : ""}`}
    >
      {/* Profile Container */}
      <div className={styles.profileContainer}>
        <div className={styles.profileImageWrapper}>
          <img src={profileImage} alt="Jess" className={styles.profileImage} />
          <span
            className={`${styles.statusBadge} ${getConnectionStatusDisplay().isOnline ? styles.online : styles.offline}`}
          >
            {getConnectionStatusDisplay().text}
          </span>
        </div>
        <div className={styles.profileInfo}>
          {profileName ? (
            <img
              src={profileName}
              alt="Jesss"
              className={styles.profileNameImage}
            />
          ) : (
            <h3 className={styles.profileName}>Jesss</h3>
          )}
        </div>
        {hasFlag("tacits") && <TacitsBadge isDarkTheme={isDarkTheme} />}
      </div>

      {/* Navbar Controls */}
      <div className={styles.navbarControls}>
        {/* Background Sync Indicator */}
        {syncingSessions && syncingSessions.size > 0 && (
          <BackgroundSyncIndicator
            syncingSessions={syncingSessions}
            isDarkTheme={isDarkTheme}
          />
        )}

        {/* Jess AI Toggle - Desktop Version */}
        <div className={styles.globalJessToggle}>
          <JessToggleButton isDarkTheme={isDarkTheme} />
        </div>

        {/* Global Language Selector - Desktop Version */}
        <div className={styles.globalLanguageSelector}>
          <LanguageSelector isDarkTheme={isDarkTheme} />
        </div>

        {/* Theme Toggle - Desktop Version */}
        <div className={styles.globalThemeToggle}>
          <ThemeToggle isDarkTheme={isDarkTheme} onToggle={onThemeToggle} />
        </div>
      </div>
    </div>
  );
};
